import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { EShortcutId } from '@/editor/controller/keyboardController/config';
import { Editor } from '@/editor';
import { createUseSyncExternalStore } from '@/utils/ui/react';
import { EKeyboardEventName } from '@/editor/controller/keyboardController';
import { useLoadingStore } from '@/stores/feedback';

const editor = Editor.getSingleton();

type TUseShortcut = Parameters<typeof editor.keyboardController.register>[0];
interface IUseShortcut extends TUseShortcut {
  /** 是否注册快捷键，默认为 `true` */
  register?: boolean;
}

/** 注册快捷建 */
export function useShortcut({ register = true, ...config }: Partial<IUseShortcut>) {
  useEffect(() => {
    if (register && config.id && config.callback) {
      return editor.keyboardController.register({
        callback: config.callback,
        id: config.id,
        disabled: config.disabled
      });
    }
  }, [config, register]);
}

export function useInModal(value: boolean) {
  useEffect(() => {
    if (value) {
      return editor.keyboardController.setInModal();
    }
  }, [value]);
}

export function useInOverlay(value: boolean) {
  useEffect(() => {
    if (value) {
      return editor.keyboardController.setInOverlay();
    }
  }, [value]);
}

export function useInLoadingOverlay() {
  const isLoadingOverlayActive = useLoadingStore((state) => state.actived);
  useInOverlay(isLoadingOverlayActive);
}

const { getSnapshot, notify, subscribe } = createUseSyncExternalStore(() => editor.keyboardController.shortcuts);
editor.keyboardController.addEventListener(EKeyboardEventName.NOTIFY_SHORTCUTS_UPDATE, notify);

function useShortcuts() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useShortcutStatus(shortcutId?: EShortcutId) {
  const shortcuts = useShortcuts();

  const shortcutStatus = useMemo(() => {
    if (!shortcutId) {
      return {
        status: false,
        label: ''
      };
    }
    const shortcut = shortcuts.find((s) => s.id === shortcutId);
    return {
      status: shortcut !== undefined && !shortcut.disabled,
      label: editor.keyboardController.getShortcutLabel(shortcutId)
    };
  }, [shortcuts, shortcutId]);

  return shortcutStatus;
}
